import { useTranslation } from "@sagot/i18n";
import { Button, cn } from "@sagot/ui";
import { useState, type MouseEvent, type ReactNode } from "react";
import { consoleClient } from "../../app/api";
import { OAUTH_PROVIDER_LIVE, type OAuthProvider } from "../auth-constants";
import { GoogleMark, MicrosoftMark } from "./brand-icons";

export type { OAuthProvider };
export type OAuthIntent = "login" | "signup" | "invite";

const PROVIDER_MARK: Record<OAuthProvider, () => ReactNode> = {
  google: () => <GoogleMark />,
  microsoft: () => <MicrosoftMark />,
};

export function oauthStartUrl(provider: OAuthProvider, intent: OAuthIntent, invite?: string): string {
  const params = new URLSearchParams({ intent });
  if (invite) params.set("invite", invite);
  return `${consoleClient.baseUrl}/v1/auth/oauth/${provider}/start?${params.toString()}`;
}

/**
 * Click-time check: the start endpoint redirects (302) when the provider is configured and answers 404 `oauth_unavailable`
 * while it is feature-flagged off (DEC-005). Asked when the button is used, never on page load.
 */
async function startsRedirect(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { redirect: "manual", credentials: "include" });
    return response.type === "opaqueredirect" || (response.status >= 300 && response.status < 400);
  } catch {
    return false;
  }
}

const isPlainPrimaryClick = (event: MouseEvent<HTMLAnchorElement>): boolean =>
  !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

interface OAuthButtonProps {
  provider: OAuthProvider;
  intent: OAuthIntent;
  label: string;
  invite?: string;
  className?: string;
  /** `solid` is the black pill (the page's lead action); `alt` the outlined pill. */
  variant?: "solid" | "alt";
}

/** Full-page redirect (no popups: FB in-app browser safe). Shows "Not configured yet" and does nothing while the provider is off. */
export function OAuthButton({ provider, intent, label, invite, className, variant = "alt" }: OAuthButtonProps) {
  const { t } = useTranslation();
  const [unavailable, setUnavailable] = useState(false);
  const [checking, setChecking] = useState(false);
  const buttonVariant = variant === "solid" ? "primary" : "secondary";
  const content = (
    <>
      {PROVIDER_MARK[provider]()}
      {label}
    </>
  );

  if (!OAUTH_PROVIDER_LIVE[provider] || unavailable) {
    return (
      <Button variant={buttonVariant} size="lg" aria-disabled="true" className={cn("cursor-not-allowed", className)}>
        {content}
        <span className="oauth-soon">({t("auth.common.notConfigured")})</span>
      </Button>
    );
  }

  const url = oauthStartUrl(provider, intent, invite);
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainPrimaryClick(event)) return;
    event.preventDefault();
    if (checking) return;
    setChecking(true);
    void startsRedirect(url).then((redirects) => {
      if (redirects) {
        window.location.assign(url);
        return;
      }
      setChecking(false);
      setUnavailable(true);
    });
  };

  return (
    <Button asChild variant={buttonVariant} size="lg" className={className}>
      <a href={url} onClick={onClick} aria-busy={checking || undefined}>
        {content}
      </a>
    </Button>
  );
}
